import React, {
  useCallback,
} from 'react';
import {
  useNotify,
} from 'core/hooks';
import {
  useGetPatientTempoStatus,
  useSendTempoLink,
} from 'pages/Dashboard/pages/Appointments/pages/List/hooks';
import Spinner from 'pages/Dashboard/components/Spinner';
import Button from 'pages/Dashboard/components/Button';
import clsx from 'clsx';
import ToolTip from 'core/components/Tooltip';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Skeleton from 'core/components/Skeleton';
import isNil from 'lodash/isNil';
import PCCStatusPill, {
  PCCStatusPillState,
} from 'pages/Dashboard/components/PCCStatusPill';

type Props = {
  patientId: number;
  disabled?: boolean;
  encounterId: number;
};

type TempoButtonProps = Props & {
  dayNumber: number;
};

function getTempoStatusPillState(
  isEligible?: boolean,
  isEnrolled?: boolean,
): PCCStatusPillState | undefined {
  let statusPillState: PCCStatusPillState | undefined;

  if (!isEligible) {
    statusPillState = 'notEligible';
  } else if (!isEnrolled) {
    statusPillState = 'notEnrolled';
  }

  return statusPillState;
}

function TempoButton({
  dayNumber,
  disabled,
  patientId,
  encounterId,
}: TempoButtonProps) {
  const notify = useNotify();
  const {
    mutateAsync: sendTempoLink,
    isPending: isLoading,
  } = useSendTempoLink(dayNumber);

  const onSendTempoLink = useCallback(async () => {
    await sendTempoLink({ patientId, encounterId });
    notify({
      variant: 'success',
      message: 'Tempo link sent.',
    });
  }, [sendTempoLink]);

  return (
    <Button
      className={clsx(
        '!py-0.5 !px-0 !min-w-[100px]',
        { '!border-blue-500': !isLoading && !disabled },
      )}
      variant="outlined"
      type="button"
      color="primary"
      onClick={onSendTempoLink}
      disabled={disabled || isLoading}
    >
      {isLoading ? (
        <div className="gap-2 flex items-center">
          <Spinner size={16} />
          Sending...
        </div>
      ) : `Send Day ${dayNumber === 0 ? '1' : '2'}`}
    </Button>
  );
}

export default function SendTempoLinkButton({
  patientId,
  disabled,
  encounterId,
}: Props) {
  const {
    data: patientTempoStatus,
    isLoading: isLoadingPatientTempoStatus,
  } = useGetPatientTempoStatus(patientId);

  const {
    isEligible,
    isEnrolled,
    hasCompletedDay1,
    hasCompletedDay2,
    isDay2Eligible,
  } = patientTempoStatus ?? {};

  const statusPillState = getTempoStatusPillState(isEligible, isEnrolled);

  const showTempoActions = !isLoadingPatientTempoStatus && isNil(statusPillState);

  return (
    <div>
      {isLoadingPatientTempoStatus ? (
        <div className="gap-2 flex items-center w-full">
          <Skeleton className="!w-52 !h-5" />
        </div>
      ) : null}
      {!isLoadingPatientTempoStatus && !isNil(statusPillState) ? (
        <PCCStatusPill state={statusPillState} />
      ) : null}
      {showTempoActions && !hasCompletedDay1 ? (
        <TempoButton
          dayNumber={0}
          disabled={disabled ?? false}
          patientId={patientId}
          encounterId={encounterId}
        />
      ) : null}
      {showTempoActions && hasCompletedDay1 ? (
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 bg-green-100 rounded-full px-2 py-0.5">
            <CheckCircleIcon className="text-teal-600 !text-base" />
            <span className="text-teal-600 text-xs font-semibold">Completed Day 1</span>
          </div>
          {hasCompletedDay2 ? (
            <div className="flex items-center gap-1 bg-green-100 rounded-full px-2 py-0.5">
              <CheckCircleIcon className="text-teal-600 !text-base" />
              <span className="text-teal-600 text-xs font-semibold">Completed Day 2</span>
            </div>
          ) : (
            <ToolTip
              title={(
                <div>
                  <p>Cannot send tempo link for day 2 because</p>
                  <p>No instruments were triggered on day 1</p>
                </div>
              )}
              hide={isDay2Eligible}
            >
              <span>
                <TempoButton
                  dayNumber={1}
                  disabled={disabled || !isDay2Eligible}
                  patientId={patientId}
                  encounterId={encounterId}
                />
              </span>
            </ToolTip>
          )}
        </div>
      ) : null}
    </div>
  );
}
